import { addError } from "@jsq/observability";
import {
  CurrencyCode,
  InvestorDiligenceStatus,
  PaymentMethod,
  VerificationStatus,
  ObjectType,
} from "#graphql/supergraphEnums";
import { toDbId } from "#utils/graphql_utils";
import {
  isPaymentMethodSupported,
  stringToPaymentMethod,
} from "../../DistributionWorkflow/utils";
import { getMoneyFromWallet } from "../../Wallet/utils";
import {
  InvalidPaymentInstructionReason,
  PositionTabStatus,
  UnableToPayReason,
} from "../StepComponents/SelectPositionsStep/SelectPositionsTable/constants";
import {
  DEFAULT_PAYMENT_CAPABILITIES,
  PaymentCapabilities,
  PaymentInstructionsStatus,
  PositionRowData,
} from "../types";
import type { GetAccountingPayableForBatchCreationQuery } from "#graphql/supergraphTypes";
import type { MoneyType } from "../../Money";

export type AccountingPayableBatch = NonNullable<
  GetAccountingPayableForBatchCreationQuery["accountingPayable"]
>;

export type AccountingPayableEntity = AccountingPayableBatch["entity"];
export type AccountingPayablePayee = AccountingPayableBatch["payees"][number];
export type AccountingPayablePayeePref = NonNullable<
  AccountingPayablePayee["position"]["paymentPref"]
>;

type AdapterOptions = {
  paymentsInstructionScreening: boolean;
};

/**
 * Generic adapter: maps AccountingPayable payees → PositionRowData[].
 */
export const mapAccountingPayableToPositionRows = (
  batch: AccountingPayableBatch,
  options: AdapterOptions,
): PositionRowData[] => {
  const paymentCapabilities: PaymentCapabilities =
    batch.entity.bankAccount?.paymentCapabilities ?? DEFAULT_PAYMENT_CAPABILITIES;

  const entityCurrency = batch.entity.currency.code as CurrencyCode;
  const jsqHostname = batch.arena.jsqHostname;

  return batch.payees.map((payee) =>
    mapPayeeToPositionRow(
      payee,
      batch.entity.name,
      entityCurrency,
      paymentCapabilities,
      jsqHostname,
      options,
    ),
  );
};

export const mapInstanceObjectType = (objectType: string): string => {
  const upperObjectType = objectType.toUpperCase();
  if (Object.values(ObjectType).includes(upperObjectType as ObjectType)) {
    return upperObjectType;
  }
  return objectType;
};

const mapPayeeToPositionRow = (
  payee: AccountingPayablePayee,
  entityName: string,
  entityCurrency: CurrencyCode,
  paymentCapabilities: PaymentCapabilities,
  jsqHostname: string,
  options: AdapterOptions,
): PositionRowData => {
  const pref = payee.position.paymentPref;
  const paymentMethod = pref?.paymentMethod ?? null;

  // Use getMoneyFromWallet to extract Money from Wallet type
  const netPayment = getMoneyFromWallet(payee.amountPayable, entityCurrency);

  const { isSelectable, unselectableReason, invalidPaymentInstructionReason } =
    getPayeeSelectability(payee, netPayment, paymentCapabilities, options);

  let positionTabStatus = PositionTabStatus.Available;
  if (payee.isMarkedPaid || payee.hasAutomatedPayment) {
    positionTabStatus = PositionTabStatus.Paid;
  } else if (!isSelectable) {
    positionTabStatus = PositionTabStatus.UnableToPay;
  }

  // Parse bankInfoRedacted for display (e.g., "Chase ····1234")
  const bankInfoParts = parseBankInfoRedacted(pref?.bankInfoRedacted);

  const accountId = payee.position.account?.id;
  let positionUrl: string | undefined;
  if (jsqHostname && accountId) {
    try {
      const dbId = toDbId(accountId);
      if (Number.isFinite(dbId)) {
        positionUrl = `https://${jsqHostname}/account/${dbId}`;
      }
    } catch (e) {
      // Malformed accountId — leave positionUrl undefined so the UI falls back to plain text.
      addError(
        JSON.stringify({
          message: "Failed to build positionUrl from accountId",
          accountId,
          error: e instanceof Error ? e.message : String(e),
        }),
      );
    }
  }

  return {
    positionId: toDbId(payee.position.id),
    positionName: payee.position.account?.gpTitle ?? "",
    netPayment,
    paymentMethod,
    paymentPrefId: pref?.id ? toDbId(pref.id) : null,
    paymentInstructionsStatus: getPaymentInstructionsStatusFromPref(pref),
    instanceId: toDbId(payee.id),
    instanceObjectType: mapInstanceObjectType(payee.objectType),
    investorName: payee.position.account?.gpTitle ?? "",
    entityName,
    positionUrl,
    bankAccountInstitutionName: bankInfoParts.institutionName,
    bankAccountLastFour: bankInfoParts.lastFour,
    isSelectable,
    unselectableReason,
    invalidPaymentInstructionReason,
    paymentInstructionsRecentlyUpdated: payee.position.hasPendingPaymentPrefChangeRequests,
    positionTabStatus,
  };
};

/**
 * Parse "Chase ····1234" into institution name and last four.
 */
const parseBankInfoRedacted = (
  bankInfoRedacted: string | null | undefined,
): { institutionName?: string; lastFour?: string } => {
  if (!bankInfoRedacted) return {};
  // bankInfoRedacted format: "Institution Name ····1234"
  const match = bankInfoRedacted.match(/^(.+?)\s+[·•]+(.{4})$/);
  if (match) {
    return { institutionName: match[1].trim(), lastFour: match[2] };
  }
  return { institutionName: bankInfoRedacted };
};

const getPayeeSelectability = (
  payee: AccountingPayablePayee,
  netPayment: MoneyType,
  paymentCapabilities: PaymentCapabilities,
  { paymentsInstructionScreening }: AdapterOptions,
): {
  isSelectable: boolean;
  unselectableReason?: UnableToPayReason;
  invalidPaymentInstructionReason?: InvalidPaymentInstructionReason;
} => {
  const pref = payee.position.paymentPref;

  if (payee.position.hasPendingPaymentPrefChangeRequests) {
    return {
      isSelectable: false,
      unselectableReason: UnableToPayReason.PendingPaymentInstructionChanges,
    };
  }

  if (payee.position.hasPassedPicrComplianceChecks === false) {
    return {
      isSelectable: false,
      unselectableReason: UnableToPayReason.PendingPicrComplianceChecks,
    };
  }

  if (!pref) {
    return { isSelectable: false, unselectableReason: UnableToPayReason.NoPaymentInstructions };
  }

  const method = stringToPaymentMethod(pref.paymentMethod);
  if (!method || !isPaymentMethodSupported(method, paymentCapabilities)) {
    return { isSelectable: false, unselectableReason: UnableToPayReason.UnsupportedMethod };
  }

  if (pref.verificationStatus === VerificationStatus.Error) {
    return {
      isSelectable: false,
      unselectableReason: UnableToPayReason.InvalidInstructions,
      invalidPaymentInstructionReason: InvalidPaymentInstructionReason.General,
    };
  }

  if (netPayment.amount <= 0) {
    return { isSelectable: false, unselectableReason: UnableToPayReason.InvalidAmount };
  }

  // Wire-specific checks
  if (
    method === PaymentMethod.Wire &&
    pref.paymentPrefFull?.__typename === "WirePaymentPrefObjectType"
  ) {
    // Check for BBI first
    const wireBbi = pref.paymentPrefFull.wireBbi;
    if (wireBbi?.field0 || wireBbi?.field1 || wireBbi?.field2 || wireBbi?.field3) {
      return {
        isSelectable: false,
        unselectableReason: UnableToPayReason.InvalidInstructions,
        invalidPaymentInstructionReason: InvalidPaymentInstructionReason.BBI,
      };
    }

    const MAX_REF_LENGTH = 16;
    if (
      pref.paymentPrefFull.referenceToBeneficiary &&
      pref.paymentPrefFull.referenceToBeneficiary.length > MAX_REF_LENGTH
    ) {
      return {
        isSelectable: false,
        unselectableReason: UnableToPayReason.InvalidInstructions,
        invalidPaymentInstructionReason:
          InvalidPaymentInstructionReason.ReferenceToBeneficiaryLength,
      };
    }
  }

  // Screening check
  if (paymentsInstructionScreening) {
    const statuses = pref.watchlistStatus ?? [];
    const allConfirmed =
      statuses.length > 0 &&
      statuses.every(
        (d) => (d.status as InvestorDiligenceStatus) === InvestorDiligenceStatus.Confirmed,
      );
    if (!allConfirmed) {
      return {
        isSelectable: false,
        unselectableReason: UnableToPayReason.PendingScreeningReview,
      };
    }
  }

  return { isSelectable: true };
};

const getPaymentInstructionsStatusFromPref = (
  pref: AccountingPayablePayeePref | null | undefined,
): PaymentInstructionsStatus => {
  if (!pref) return PaymentInstructionsStatus.Missing;
  if (pref.verificationStatus === VerificationStatus.Error)
    return PaymentInstructionsStatus.Invalid;
  return PaymentInstructionsStatus.Valid;
};
